import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, Download, Filter, Search, FileText, Calendar, User, ShieldCheck, Info } from 'lucide-react';
import ComplianceFooter from './ComplianceFooter';
import { Document } from '../types';

interface DataRoomProps {
  onDataRoomAccessed: () => void;
  dataRoomAccessed: boolean;
}

const DataRoom: React.FC<DataRoomProps> = ({ onDataRoomAccessed, dataRoomAccessed }) => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showCompliance, setShowCompliance] = useState(false);
  const [errorModal, setErrorModal] = useState<{ open: boolean, message: string }>({ open: false, message: '' });

  // Fetch documents from server
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const token = localStorage.getItem('token');
        console.log('🔧 DEBUG: Fetching documents from server...');
        console.log('🔧 DEBUG: Token available:', !!token);
        
        const response = await fetch('https://data-room-api.onrender.com/api/documents', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('🔧 DEBUG: Response status:', response.status);
        console.log('🔧 DEBUG: Response ok:', response.ok);
        
        if (response.ok) {
          const serverDocuments = await response.json();
          console.log('🔧 DEBUG: Server documents received:', serverDocuments);
          setDocuments(serverDocuments);
          setFilteredDocuments(serverDocuments);
        } else {
          console.error('🔧 DEBUG: Failed to fetch documents from server');
          console.error('🔧 DEBUG: Response status:', response.status);
          // Don't use fallback - show error instead
          setErrorModal({
            open: true,
            message: `Failed to fetch documents from server. Status: ${response.status}`
          });
          setDocuments([]);
          setFilteredDocuments([]);
        }
      } catch (error) {
        console.error('🔧 DEBUG: Error fetching documents:', error);
        // Don't use fallback - show error instead
        setErrorModal({
          open: true,
          message: `Error fetching documents: ${error}`
        });
        setDocuments([]);
        setFilteredDocuments([]);
      } finally {
        setIsLoading(false);
        onDataRoomAccessed();
      }
    };

    fetchDocuments();
  }, [onDataRoomAccessed]);

  useEffect(() => {
    let filtered = documents;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(doc => doc.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(doc =>
        doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.owner.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredDocuments(filtered);
  }, [documents, selectedCategory, searchTerm]);

  const categories = ['all', 'Financials', 'Legal'];

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return <FileText className="w-6 h-6" color="#cba864" />;
      case 'doc':
        return <FileText className="w-6 h-6" color="#cba864" />;
      case 'xls':
        return <FileText className="w-6 h-6" color="#cba864" />;
      case 'ppt':
        return <FileText className="w-6 h-6" color="#cba864" />;
      default:
        return <FileText className="w-6 h-6" color="#cba864" />;
    }
  };

  const statusMessages: Record<number, string> = {
    400: 'Bad Request: The request was invalid.',
    401: 'Unauthorized: Access token required or invalid. Please log in.',
    403: 'Forbidden: You do not have permission to access this file.',
    404: 'Not Found: The file does not exist on the server.',
    500: 'Server Error: An error occurred on the server.',
  };

  const handleDownloadDocument = async (document: Document) => {
    if (!document.downloadAllowed) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`https://data-room-api.onrender.com/api/documents/download/${document.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        let errorMsg = '';
        try {
          const error = await response.json();
          errorMsg = error.error || '';
        } catch {
          try {
            const text = await response.text();
            if (text) errorMsg = text;
          } catch {}
        }
        const statusExplanation = statusMessages[response.status] || 'Unknown error';
        setErrorModal({
          open: true,
          message: `${errorMsg ? errorMsg + ' — ' : ''}Status: ${response.status} (${statusExplanation})`
        });
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${document.name}.${document.type}`;
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setErrorModal({
        open: true,
        message: `Download failed: ${err?.message || err}`
      });
    }
  };

  // Add this function to handle download all
  const handleDownloadAll = () => {
    filteredDocuments.forEach((document) => {
      if (document.downloadAllowed) {
        handleDownloadDocument(document);
      }
    });
  };

  const complianceStatement = `Our platform is designed to support electronic signatures in accordance with the EU eIDAS Regulation (EU) No 910/2014, including Simple (SES), Advanced (AdES), and Qualified (QES) Electronic Signatures where applicable. We integrate with Qualified Trust Service Providers (QTSPs) and use certified signature creation devices to promote security and compliance with relevant ETSI technical standards.

Signatures may include trusted timestamping, certificate validation, and audit trails to enhance reliability. We adhere to applicable data protection laws, including GDPR, ensuring data encryption and user privacy.

While we strive to comply with all relevant regulations and standards, the legal validity and enforceability of electronic signatures may vary depending on jurisdiction and specific use cases. Users are encouraged to seek independent legal advice to confirm acceptance in their context.`;

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="card">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-neutral-600">Loading data room...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-6xl mx-auto"
    >
      <div className="card">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <FolderOpen className="w-8 h-8 text-primary-600" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            Access Granted:
          </h2>
          <h3 className="text-xl font-semibold text-neutral-800 mb-2">
            Welcome to the Data Room
          </h3>
          <p className="text-neutral-600">
            You now have access to curated documents for review. All downloads and interactions are logged and monitored for compliance.
          </p>
        </div>

        {/* Filters and Search */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-neutral-500" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="input-field"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm text-neutral-600">
            <span>{filteredDocuments.length} documents found</span>
            <span>Last accessed: {new Date().toLocaleDateString()}</span>
          </div>
        </div>

        {/* Download All Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={handleDownloadAll}
            className="btn-primary flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Download All
          </button>
        </div>

        {/* Documents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredDocuments.map((document) => (
            <motion.div
              key={document.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <span className="text-2xl">{getFileIcon(document.type)}</span>
                <span className={`px-2 py-1 text-xs rounded-full`} style={{backgroundColor: '#cba864', color: '#fff'}}>
                  {document.status}
                </span>
              </div>

              <h3 className="font-medium text-neutral-900 mb-2 line-clamp-2">
                {document.name}
              </h3>

              <div className="space-y-1 text-sm text-neutral-600 mb-4">
                <div className="flex items-center">
                  <span className="w-32 flex-shrink-0">Category:</span>
                  <span className="font-medium">{document.category}</span>
                </div>
                <div className="flex items-center">
                  <span className="w-32 flex-shrink-0">Size:</span>
                  <span>{document.size}</span>
                </div>
                <div className="flex items-center">
                  <span className="w-32 flex-shrink-0 flex items-center"><Calendar className="w-4 h-4 mr-1" /> Last Updated:</span>
                  <span>{document.lastUpdated}</span>
                </div>
                <div className="flex items-center">
                  <span className="w-32 flex-shrink-0 flex items-center"><User className="w-4 h-4 mr-1" /> Owner:</span>
                  <span>{document.owner}</span>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleDownloadDocument(document)}
                  className="flex-1 btn-primary text-sm py-2 flex items-center justify-center"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredDocuments.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-600">No documents found matching your criteria.</p>
          </div>
        )}

        {/* Centered Complete Review Button */}
        <div className="flex justify-center mt-8">
          <button
            onClick={() => navigate('/completion')}
            className="btn-primary"
          >
            Complete Review & Continue
          </button>
        </div>

        <ComplianceFooter />
      </div>

      {errorModal.open && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: '#fff',
            border: '2px solid #cba864',
            color: '#cba864',
            padding: '2rem',
            borderRadius: '12px',
            minWidth: '320px',
            textAlign: 'center',
            fontWeight: 'bold'
          }}>
            <div style={{ marginBottom: '1rem' }}>{errorModal.message}</div>
            <button
              style={{
                background: '#cba864',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '0.5rem 1.5rem',
                cursor: 'pointer'
              }}
              onClick={() => setErrorModal({ open: false, message: '' })}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default DataRoom; 